import { useState, useMemo, useRef } from 'react';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import {
  calcularPontosCiclo,
  calcularProgressao,
  calcularLimiarFuncionario,
  aplicaRegraEspecial18Anos,
  PONTOS_REGRA_18_ANOS,
  pontosParaAvaliacao,
  PONTOS_HISTORICO,
  type AvaliacaoSiadap,
  type PontosEscalasConfig,
} from '@/utils/progressaoUtils';

interface ExportarRelatoriosModalProps {
  onClose: () => void;
}

const avaliacaoConfig: Record<string, { bg: string; text: string }> = {
  Excelente:   { bg: '#d1fae5', text: '#065f46' },
  'Muito Bom': { bg: '#fef3c7', text: '#92400e' },
  Bom:         { bg: '#f4f4f5', text: '#52525b' },
  Regular:     { bg: '#fff7ed', text: '#9a3412' },
  Inadequado:  { bg: '#fff1f2', text: '#9f1239' },
  Adequado:    { bg: '#f4f4f5', text: '#52525b' },
  Relevante:   { bg: '#f0fdfa', text: '#134e4a' },
};

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('');
}

function calcularAnosServico(dataEntrada?: string): number | null {
  if (!dataEntrada) return null;
  const entrada = new Date(dataEntrada);
  if (isNaN(entrada.getTime())) return null;
  const hoje = new Date();
  let anos = hoje.getFullYear() - entrada.getFullYear();
  if (hoje.getMonth() < entrada.getMonth() || (hoje.getMonth() === entrada.getMonth() && hoje.getDate() < entrada.getDate())) anos -= 1;
  return Math.max(0, anos);
}

export default function ExportarRelatoriosModal({ onClose }: ExportarRelatoriosModalProps) {
  const { animating, handleClose } = useModalAnimation(onClose);
  const { funcionarios } = useFuncionarios();
  const { registosSiadap, registosHistorico, pontosDescontadosPorFuncionario, progressoes } = useSiadap();
  const { settings } = useAppSettings();
  const printRef = useRef<HTMLDivElement>(null);

  const unidades = useMemo(() => Array.from(new Set(funcionarios.map((f) => f.unidadeOrganica).filter(Boolean))).sort() as string[], [funcionarios]);
  const carreiras = useMemo(() => Array.from(new Set(funcionarios.map((f) => f.carreira))).sort(), [funcionarios]);

  const [unidadeFilter, setUnidadeFilter] = useState('');
  const [carreiraFilter, setCarreiraFilter] = useState('');
  const [elegibilidadeFilter, setElegibilidadeFilter] = useState<'todos' | 'elegiveis'>('todos');
  const [incluirSiadap, setIncluirSiadap] = useState(true);
  const funcionariosComAcelerador = useMemo(() => {
    const set = new Set<string>();
    progressoes.forEach((p) => {
      if (p.aceleradorRemuneratorio) set.add(p.funcionarioId);
    });
    return set;
  }, [progressoes]);
  const [incluirProgressoes, setIncluirProgressoes] = useState(true);

  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente: settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom: settings.pontos_bom,
    Regular: settings.pontos_regular,
    Inadequado: settings.pontos_inadequado,
  }), [settings]);

  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente: settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom: settings.pontos_bom,
      Regular: settings.pontos_regular,
      Inadequado: settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente: settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom: settings.pontos_bom_2023,
      Regular: settings.pontos_regular_2023,
      Inadequado: settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente: settings.pontos_excelente_pre2023,
      Relevante: settings.pontos_relevante_pre2023,
      Adequado: settings.pontos_adequado_pre2023,
      Inadequado: settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom: settings.pontos_adequado_pre2023,
      Regular: settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const dadosFuncionarios = useMemo(() => {
    return funcionarios.map((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
      const pontosDescontados = pontosDescontadosPorFuncionario[f.id] ?? 0;
      const isPrepav = f.excecaoPrepav === true;
      const temRegistos = siadapDoFunc.length > 0 || historicoDoFunc.length > 0;
      // Pontos manuais são SEMPRE somados
      const pontosManuais = f.pontosAtuais ?? 0;

      const pontosTotal = calcularPontosCiclo(siadapDoFunc, historicoDoFunc, pontosDescontados, pontosManuais, pontosMap, undefined, escalas);
      const { limiar } = calcularLimiarFuncionario(siadapDoFunc, historicoDoFunc, settings.pontos_progressao || 8, settings.pontos_progressao_pre2025 || 10, settings.pontos_progressao_2023 || 8, escalas);
      const jaProgrediu = (pontosDescontadosPorFuncionario[f.id] ?? 0) > 0;
      const regraEspecial = aplicaRegraEspecial18Anos(f.dataEntradaFuncaoPublica, pontosTotal, jaProgrediu, 2024, limiar);
      const limiarEfetivo = regraEspecial ? PONTOS_REGRA_18_ANOS : limiar;
      const prog = calcularProgressao(pontosTotal, f.nivelAtual, limiarEfetivo);

      const pontosSiadap = siadapDoFunc.reduce((s, r) => s + pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', pontosMap, escalas), 0);
      const pontosHistorico = historicoDoFunc.reduce((s, r) => s + (PONTOS_HISTORICO[r.classificacaoServico] ?? 0), 0);

      const avaliacoesSiadap = [...siadapDoFunc].sort((a, b) => {
        const anoA = parseInt((a.periodoAvaliacao ?? '').split('-').pop() ?? '0', 10);
        const anoB = parseInt((b.periodoAvaliacao ?? '').split('-').pop() ?? '0', 10);
        return anoB - anoA;
      }).map((r) => ({ ...r, pontos: pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', pontosMap, escalas) }));

      return { funcionario: f, pontosTotal, pontosDescontados, pontosSiadap, pontosHistorico, limiarEfetivo, regraEspecial, status: prog.status, percentagem: prog.percentagem, avaliacoesSiadap };
    });
  }, [funcionarios, registosSiadap, registosHistorico, pontosDescontadosPorFuncionario, pontosMap, escalas, settings]);

  const filtrados = useMemo(() => {
    return dadosFuncionarios.filter((d) => {
      const f = d.funcionario;
      const matchUnidade = unidadeFilter === '' || f.unidadeOrganica === unidadeFilter;
      const matchCarreira = carreiraFilter === '' || f.carreira === carreiraFilter;
      const matchEleg = elegibilidadeFilter === 'todos' || d.status === 'pronto';
      return matchUnidade && matchCarreira && matchEleg;
    }).sort((a, b) => a.funcionario.nome.localeCompare(b.funcionario.nome));
  }, [dadosFuncionarios, unidadeFilter, carreiraFilter, elegibilidadeFilter]);

  function handlePrint() {
    window.print();
  }

  const tituloRelatorio = unidadeFilter
    ? `Relatório Consolidado — ${unidadeFilter}`
    : carreiraFilter
      ? `Relatório Consolidado — ${carreiraFilter}`
      : 'Relatório Consolidado — Todos os Funcionários';

  return (
    <>
      <style>{`
        @media print {
          .no-print-modal { display: none !important; }
          .print-content { position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; z-index: 9999 !important; background: white !important; }
          body > * { display: none !important; }
          body > .print-content { display: block !important; }
        }
      `}</style>

      {/* Overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 no-print-modal" onClick={handleClose}>
        <div className={`bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-zinc-900">
                <i className="ri-file-chart-2-line text-white text-base" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold text-zinc-900">Exportar Relatórios</h2>
                <p className="text-[12px] text-zinc-400">Relatório consolidado por unidade ou carreira</p>
              </div>
            </div>
            <button onClick={handleClose} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Filtros */}
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Unidade Orgânica</label>
                <select
                  value={unidadeFilter}
                  onChange={(e) => setUnidadeFilter(e.target.value)}
                  className="w-full text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                >
                  <option value="">Todas as Unidades</option>
                  {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Carreira</label>
                <select
                  value={carreiraFilter}
                  onChange={(e) => setCarreiraFilter(e.target.value)}
                  className="w-full text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                >
                  <option value="">Todas as Carreiras</option>
                  {carreiras.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Elegibilidade</label>
              <div className="flex items-center gap-2">
                {([
                  { key: 'todos', label: 'Todos os funcionários' },
                  { key: 'elegiveis', label: 'Apenas elegíveis' },
                ] as { key: typeof elegibilidadeFilter; label: string }[]).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setElegibilidadeFilter(opt.key)}
                    className={`whitespace-nowrap flex-1 text-[12px] font-medium py-2 rounded-lg transition-all cursor-pointer ${
                      elegibilidadeFilter === opt.key ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Incluir no relatório</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={incluirSiadap} onChange={(e) => setIncluirSiadap(e.target.checked)} className="w-4 h-4 rounded cursor-pointer" />
                  <span className="text-[13px] text-zinc-700">Histórico SIADAP</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={incluirProgressoes} onChange={(e) => setIncluirProgressoes(e.target.checked)} className="w-4 h-4 rounded cursor-pointer" />
                  <span className="text-[13px] text-zinc-700">Progressões</span>
                </label>
              </div>
            </div>

            {/* Preview count */}
            <div className="bg-zinc-50 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-zinc-200 rounded-lg">
                  <i className="ri-team-line text-zinc-600 text-sm" />
                </div>
                <div>
                  <p className="text-[13px] font-bold text-zinc-800">{filtrados.length} funcionário(s)</p>
                  <p className="text-[11px] text-zinc-400">serão incluídos no relatório</p>
                </div>
              </div>
              {filtrados.filter((d) => d.status === 'pronto').length > 0 && (
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
                  {filtrados.filter((d) => d.status === 'pronto').length} elegíveis
                </span>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2">
            <button onClick={handleClose} className="whitespace-nowrap text-[13px] font-medium text-zinc-500 hover:text-zinc-800 px-4 py-2.5 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer">
              Cancelar
            </button>
            <button
              onClick={handlePrint}
              disabled={filtrados.length === 0}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-5 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-printer-line text-sm" />
              Imprimir / PDF
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo para impressão */}
      <div ref={printRef} className="print-content hidden print:block" style={{ display: 'none' }}>
        <style>{`
          @media print {
            .print-content { display: block !important; padding: 20px; font-family: sans-serif; }
            .page-break { page-break-after: always; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { padding: 6px 8px; border: 1px solid #e4e4e7; text-align: left; }
            th { background: #f4f4f5; font-weight: 600; }
          }
        `}</style>
        <div style={{ padding: '24px', fontFamily: 'sans-serif', fontSize: '12px', color: '#18181b' }}>
          {/* Cabeçalho do relatório consolidado */}
          <div style={{ borderBottom: '2px solid #18181b', paddingBottom: '12px', marginBottom: '20px' }}>
            <p style={{ fontSize: '10px', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>
              {settings.nome_camara} · SIADAP para RH
            </p>
            <h1 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 4px' }}>{tituloRelatorio}</h1>
            <p style={{ fontSize: '11px', color: '#71717a', margin: 0 }}>
              Gerado em {new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })} · Ciclo {settings.ciclo_siadap} · {filtrados.length} funcionário(s)
            </p>
          </div>

          {/* Tabela resumo */}
          <h2 style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Resumo Geral</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginBottom: '24px' }}>
            <thead>
              <tr style={{ background: '#f4f4f5' }}>
                <th style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'left' }}>Funcionário</th>
                <th style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'left' }}>Carreira</th>
                <th style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'left' }}>Nível</th>
                <th style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'right' }}>Pontos</th>
                <th style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'right' }}>Limiar</th>
                <th style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'center' }}>Estado</th>
                <th style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'center' }}>Acelerador</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((d, i) => {
                const f = d.funcionario;
                const statusLabel = d.status === 'pronto' ? 'Elegível' : d.status === 'proximo' ? 'Próximo' : 'Em Progresso';
                const statusColor = d.status === 'pronto' ? '#065f46' : d.status === 'proximo' ? '#92400e' : '#52525b';
                return (
                  <tr key={f.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '6px 8px', border: '1px solid #e4e4e7' }}>
                      <strong>{f.nome}</strong>
                      <span style={{ color: '#71717a', marginLeft: '6px', fontSize: '10px' }}>#{f.id}</span>
                    </td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e4e4e7' }}>{f.carreira}</td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e4e4e7' }}>Nível {f.nivelAtual}</td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'right', fontWeight: 700 }}>
                      {d.pontosTotal % 1 === 0 ? d.pontosTotal.toFixed(0) : d.pontosTotal.toFixed(1)}
                    </td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'right' }}>{d.limiarEfetivo}</td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'center', color: statusColor, fontWeight: 600 }}>
                      {statusLabel}
                      {d.regraEspecial && ' (18 anos)'}
                    </td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e4e4e7', textAlign: 'center' }}>
                      {funcionariosComAcelerador.has(f.id) ? (
                        <span style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', padding: '1px 6px', borderRadius: '99px', fontWeight: 600, fontSize: '10px' }}>Acelerador</span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Detalhe por funcionário */}
          {filtrados.map((d, idx) => {
            const f = d.funcionario;
            const anosServico = calcularAnosServico(f.dataEntradaFuncaoPublica);
            const initials = getInitials(f.nome);
            return (
              <div key={f.id} style={{ marginBottom: '32px', pageBreakInside: 'avoid' }}>
                {idx > 0 && <div style={{ borderTop: '1px dashed #d4d4d8', marginBottom: '24px' }} />}

                {/* Cabeçalho do funcionário */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#f4f4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px', flexShrink: 0 }}>
                    {initials}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 2px' }}>{f.nome}</h3>
                    <p style={{ fontSize: '11px', color: '#71717a', margin: '0 0 6px' }}>
                      {f.carreira} · #{f.id} · Nível {f.nivelAtual}
                      {f.unidadeOrganica && ` · ${f.unidadeOrganica}`}
                      {anosServico !== null && ` · ${anosServico} anos de serviço`}
                      {funcionariosComAcelerador.has(f.id) && ' · Acelerador Remuneratório'}
                    </p>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '11px' }}>
                      <span>Pontos: <strong>{d.pontosTotal % 1 === 0 ? d.pontosTotal.toFixed(0) : d.pontosTotal.toFixed(1)}</strong> / {d.limiarEfetivo}</span>
                      <span>SIADAP: <strong>{d.pontosSiadap % 1 === 0 ? d.pontosSiadap.toFixed(0) : d.pontosSiadap.toFixed(1)}</strong></span>
                      {d.pontosHistorico > 0 && <span>Histórico: <strong>{d.pontosHistorico % 1 === 0 ? d.pontosHistorico.toFixed(0) : d.pontosHistorico.toFixed(1)}</strong></span>}
                      {d.pontosDescontados > 0 && <span>Descontados: <strong>-{d.pontosDescontados % 1 === 0 ? d.pontosDescontados.toFixed(0) : d.pontosDescontados.toFixed(1)}</strong></span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 700,
                      background: d.status === 'pronto' ? '#d1fae5' : d.status === 'proximo' ? '#fef3c7' : '#f4f4f5',
                      color: d.status === 'pronto' ? '#065f46' : d.status === 'proximo' ? '#92400e' : '#52525b',
                    }}>
                      {d.status === 'pronto' ? 'Elegível' : d.status === 'proximo' ? 'Próximo' : 'Em Progresso'}
                    </span>
                    <p style={{ fontSize: '10px', color: '#71717a', margin: '4px 0 0' }}>{d.percentagem.toFixed(0)}% do limiar</p>
                  </div>
                </div>

                {/* Histórico SIADAP */}
                {incluirSiadap && d.avaliacoesSiadap.length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#71717a', marginBottom: '4px' }}>Histórico SIADAP</p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                      <thead>
                        <tr style={{ background: '#f4f4f5' }}>
                          <th style={{ padding: '4px 6px', border: '1px solid #e4e4e7', textAlign: 'left' }}>Período</th>
                          <th style={{ padding: '4px 6px', border: '1px solid #e4e4e7', textAlign: 'left' }}>Avaliação</th>
                          <th style={{ padding: '4px 6px', border: '1px solid #e4e4e7', textAlign: 'right' }}>Chefe</th>
                          <th style={{ padding: '4px 6px', border: '1px solid #e4e4e7', textAlign: 'right' }}>CCA</th>
                          <th style={{ padding: '4px 6px', border: '1px solid #e4e4e7', textAlign: 'right' }}>Pontos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.avaliacoesSiadap.map((r, i) => {
                          const cfg = avaliacaoConfig[r.avaliacao] ?? avaliacaoConfig['Adequado'];
                          return (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ padding: '4px 6px', border: '1px solid #e4e4e7' }}>{r.periodoAvaliacao || '—'}</td>
                              <td style={{ padding: '4px 6px', border: '1px solid #e4e4e7' }}>
                                <span style={{ background: cfg.bg, color: cfg.text, padding: '1px 6px', borderRadius: '99px', fontWeight: 600 }}>{r.avaliacao}</span>
                              </td>
                              <td style={{ padding: '4px 6px', border: '1px solid #e4e4e7', textAlign: 'right' }}>{r.pontosChefe.toFixed(1)}</td>
                              <td style={{ padding: '4px 6px', border: '1px solid #e4e4e7', textAlign: 'right' }}>{r.pontosCCA.toFixed(1)}</td>
                              <td style={{ padding: '4px 6px', border: '1px solid #e4e4e7', textAlign: 'right', fontWeight: 700 }}>
                                {r.pontos % 1 === 0 ? r.pontos.toFixed(0) : r.pontos.toFixed(1)} pts
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Rodapé */}
          <div style={{ borderTop: '1px solid #e4e4e7', paddingTop: '12px', marginTop: '16px', textAlign: 'center', fontSize: '10px', color: '#a1a1aa' }}>
            {settings.nome_camara} · SIADAP para RH · Ciclo {settings.ciclo_siadap} · Documento gerado automaticamente
          </div>
        </div>
      </div>
    </>
  );
}
